import React, { FC, useMemo, useState } from 'react';
import { useGetIngestServerGroups } from '../../../api/serverGroup';
import { IngestServerGroup, Status } from '../../../types/tecton_proto/servergroupservice/server_group_service';
import {
  ExtendedTableColumn,
  Loading,
  Table,
  Empty,
  TableViewLayout,
  Input,
  FlexGroup,
  ComboFilter,
  ComboFilterCategory,
  ComboFilterListSelector,
  ComboFilterDateSelector,
  Popover,
  Button,
  Link,
  useTectonTheme,
  Text,
  Avatar,
  FlexItem,
  Select,
  useTableFilters,
  CopyButton,
} from '@lithos';
import { useParams } from 'react-router-dom';
import { uniq } from 'lodash';
import ServerGroupStatusBadge from './ServerGroupStatusBadge';
import DateCell from '../fcoListView/DateCell';
import { Routes as AppRoutes } from '../../../core/routes';
import { defaultDateState } from '../fcoListView/utils';
import { getFilteredItems, isInDateRange, tableStringSortParameters } from '@lithos/TableUtils';

enum IngestServerGroupsTableColumnKeys {
  STATUS,
  NAME,
  ID,
  OWNER,
  CREATED_AT,
  LAST_UPDATED_AT,
}

interface IngestServerGroupsTableFilterState {
  Status: string[];
  Name: string[];
  Owner: string[];
  Created: [Date, Date];
  'Last Updated': [Date, Date];
}

const ColumnsPopover = ({ columnsSelector }: { columnsSelector: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Popover
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      body={columnsSelector}
      trigger={<Button type="secondary" label="Columns" icon="Columns" testId="ingest-server-groups-columns-button" />}
    />
  );
};

const SearchInput: FC<{
  hasAnyFilters: boolean;
  activeFilterCount: number;
  handleResetFilters: () => void;
  onSearchInputChange: (value: string) => void;
  categories: ComboFilterCategory[];
}> = ({ hasAnyFilters, activeFilterCount, handleResetFilters, onSearchInputChange, categories }) => {
  const [selectedCategory, setSelectedCategory] = useState<ComboFilterCategory | undefined>(undefined);
  const [searchQuery, _] = useState<string | undefined>(undefined);

  return (
    <FlexGroup padding="0">
      <Input
        value={searchQuery}
        onChange={onSearchInputChange}
        icon="Search"
        placeholder="Search by name or ID"
        type="string"
        size="s"
        disabled={false}
        testId={'search-ingest-server-groups-table'}
      />
      <ComboFilter
        icon={'Filters'}
        hasAnyActiveFilters={hasAnyFilters}
        activeFilterCount={activeFilterCount}
        resetFilters={handleResetFilters}
        categories={categories}
        setSelectedCategory={setSelectedCategory}
        selectedCategory={selectedCategory}
        testId={'filter-ingest-server-groups-table'}
      />
    </FlexGroup>
  );
};

const IngestServerGroupsTableContent: FC<{ serverGroups: IngestServerGroup[] }> = ({ serverGroups }) => {
  const { workspace } = useParams();
  const { theme } = useTectonTheme();

  const defaultFilterState: IngestServerGroupsTableFilterState = useMemo(() => {
    return {
      Status: [],
      Name: [],
      Owner: [],
      Created: defaultDateState(serverGroups, 'created_at').map((date) => new Date(date)) as [Date, Date],
      'Last Updated': defaultDateState(serverGroups, 'updated_at').map((date) => new Date(date)) as [Date, Date],
    };
  }, [serverGroups]);

  const {
    searchQuery,
    hasAnyFilters,
    activeFilterCount,
    sortType,
    setSortType,
    activeHeading,
    setActiveHeading,
    filterState,
    onSearchInputChange,
    handleResetFilters,
    handleFilterChange,
  } = useTableFilters<IngestServerGroup, IngestServerGroupsTableColumnKeys, IngestServerGroupsTableFilterState>(
    defaultFilterState
  );

  const filterComponents: Record<
    IngestServerGroupsTableColumnKeys,
    {
      filter: React.ReactNode;
      activeCount: () => number;
      filterCallback: (entity: IngestServerGroup) => boolean;
      name: string;
    }
  > = useMemo(() => {
    return {
      [IngestServerGroupsTableColumnKeys.STATUS]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState.Status}
            setSelected={(type) => handleFilterChange(filterComponents, 'Status', type)}
            items={uniq(
              serverGroups.map((serverGroup) => {
                const status = serverGroup.status ?? Status.UNSPECIFIED;
                return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
              })
            ).sort()}
            title={''}
          />
        ),
        activeCount: () => filterState.Status.length,
        filterCallback: (serverGroup: IngestServerGroup) => {
          return filterState.Status.map((status) => status.toUpperCase()).includes(
            serverGroup.status ?? Status.UNSPECIFIED
          );
        },
        name: 'Status',
      },
      [IngestServerGroupsTableColumnKeys.NAME]: {
        filter: <>Filter</>,
        activeCount: () => filterState.Name.length,
        filterCallback: (serverGroup: IngestServerGroup) => {
          return filterState.Name.includes(serverGroup.name ?? '');
        },
        name: 'Name',
      },
      [IngestServerGroupsTableColumnKeys.ID]: {
        filter: <></>,
        activeCount: () => 0,
        filterCallback: () => {
          return true;
        },
        name: 'ID',
      },
      [IngestServerGroupsTableColumnKeys.OWNER]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState.Owner}
            setSelected={(type) => handleFilterChange(filterComponents, 'Owner', type)}
            items={uniq(serverGroups.map((serverGroup) => serverGroup.metadata?.owner ?? '') ?? [])
              .filter((owner) => owner !== '')
              .sort()}
            icon="UserCircle"
            title={''}
          />
        ),
        activeCount: () => filterState.Owner.length,
        filterCallback: (serverGroup: IngestServerGroup) => {
          return filterState.Owner.includes(serverGroup.metadata?.owner ?? '');
        },
        name: 'Owner',
      },
      [IngestServerGroupsTableColumnKeys.CREATED_AT]: {
        filter: (
          <ComboFilterDateSelector
            minDate={defaultFilterState.Created[0]}
            maxDate={defaultFilterState.Created[1]}
            start={filterState['Created'][0]}
            setStart={(date) => {
              handleFilterChange(filterComponents, 'Created', [date as Date, filterState['Created'][1] as Date]);
            }}
            end={filterState['Created'][1]}
            setEnd={(date) => {
              handleFilterChange(filterComponents, 'Created', [filterState['Created'][0] as Date, date as Date]);
            }}
          />
        ),
        activeCount: () => filterState.Created.length,
        filterCallback: (serverGroup: IngestServerGroup) => {
          return isInDateRange(new Date(serverGroup.created_at ?? ''), filterState.Created);
        },
        name: 'Created At',
      },
      [IngestServerGroupsTableColumnKeys.LAST_UPDATED_AT]: {
        filter: (
          <ComboFilterDateSelector
            minDate={defaultFilterState['Last Updated'][0]}
            maxDate={defaultFilterState['Last Updated'][1]}
            start={filterState['Last Updated'][0]}
            setStart={(date) => {
              handleFilterChange(filterComponents, 'Last Updated', [date as Date, filterState['Last Updated'][1]]);
            }}
            end={filterState['Last Updated'][1]}
            setEnd={(date) => {
              handleFilterChange(filterComponents, 'Last Updated', [filterState['Last Updated'][0], date as Date]);
            }}
          />
        ),
        activeCount: () => filterState['Last Updated'].length,
        filterCallback: (serverGroup: IngestServerGroup) => {
          return isInDateRange(new Date(serverGroup.updated_at ?? ''), filterState['Last Updated']);
        },
        name: 'Last Updated At',
      },
    };
  }, [filterState, handleFilterChange, serverGroups, defaultFilterState]);

  const columns: ExtendedTableColumn<IngestServerGroup & { id: string }>[] = [
    {
      name: 'Status',
      id: IngestServerGroupsTableColumnKeys.STATUS,
      columnWidth: 's',
      content: (serverGroup: IngestServerGroup) => {
        return <ServerGroupStatusBadge status={serverGroup.status ?? Status.UNSPECIFIED} />;
      },
      ...tableStringSortParameters(sortType, 'status', setSortType),
    },
    {
      name: 'Name',
      id: IngestServerGroupsTableColumnKeys.NAME,
      columnWidth: 'm',
      content: (serverGroup: IngestServerGroup) => {
        return (
          <Link
            icon="IngestServerGroup"
            name={serverGroup.name}
            url={
              AppRoutes.ingestServerGroup
                .replace(':id', serverGroup.id ?? '')
                .replace(':workspace', workspace ?? '')
                .replace('*', '') + '/metadata'
            }
          />
        );
      },
      ...tableStringSortParameters(sortType, 'name', setSortType),
    },
    {
      name: 'ID',
      id: IngestServerGroupsTableColumnKeys.ID,
      content: (serverGroup: IngestServerGroup) => {
        return (
          <FlexGroup padding="0" gapSize="2" direction="row" alignItems="center">
            <FlexItem grow={0}>
              <Text color={theme.v1.colors.text.subduedText} code>
                {serverGroup.id}
              </Text>
            </FlexItem>
            <FlexItem grow={0}>
              <CopyButton code={serverGroup.id ?? ''} />
            </FlexItem>
          </FlexGroup>
        );
      },
      columnWidth: 'xl',
      ...tableStringSortParameters(sortType, 'id', setSortType),
    },
    {
      name: 'Owner',
      id: IngestServerGroupsTableColumnKeys.OWNER,
      columnWidth: 'm',
      content: (serverGroup: IngestServerGroup) => {
        if (!serverGroup.metadata?.owner) {
          return <>-</>;
        }
        return (
          <FlexGroup padding="0" alignItems="center" gapSize={'1'}>
            <Avatar name={serverGroup.metadata?.owner} />
            <FlexItem>{serverGroup.metadata?.owner}</FlexItem>
          </FlexGroup>
        );
      },
    },
    {
      name: 'Created',
      id: IngestServerGroupsTableColumnKeys.CREATED_AT,
      columnWidth: 'm',
      content: (serverGroup: IngestServerGroup) => {
        return <DateCell date={new Date(serverGroup.created_at ?? '')} />;
      },
    },
    {
      name: 'Last Updated',
      id: IngestServerGroupsTableColumnKeys.LAST_UPDATED_AT,
      columnWidth: 'm',
      content: (serverGroup: IngestServerGroup) => {
        return <DateCell date={new Date(serverGroup.updated_at ?? '')} />;
      },
    },
  ];

  const categories: ComboFilterCategory[] = [
    {
      name: 'Status',
      icon: 'AccountsAndAccess',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'Status', defaultFilterState.Status);
      },
      popover: filterComponents[IngestServerGroupsTableColumnKeys.STATUS].filter,
    },
    {
      name: 'Owner',
      icon: 'AccountsAndAccess',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'Owner', defaultFilterState.Owner);
      },
      popover: filterComponents[IngestServerGroupsTableColumnKeys.OWNER].filter,
    },
    {
      name: 'Created At',
      icon: 'Calendar',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'Created', defaultFilterState.Created);
      },
      popover: filterComponents[IngestServerGroupsTableColumnKeys.CREATED_AT].filter,
    },
    {
      name: 'Last Updated At',
      icon: 'Calendar',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'Last Updated', defaultFilterState['Last Updated']);
      },
      popover: filterComponents[IngestServerGroupsTableColumnKeys.LAST_UPDATED_AT].filter,
    },
  ];

  const selectableColumns = columns.map((column) => {
    return { name: column.name as string, visible: true };
  });

  const [columnState, setColumnState] = useState<{ name: string; visible: boolean }[]>(selectableColumns);

  const columnsSelector = (
    <Select
      groups={[
        {
          items: columnState.map((column) => {
            return {
              name: column.name,
              asToggle: true,
              onClick: () => {
                const newColumnState = columnState.map((selectableColumn) => {
                  if (selectableColumn.name !== column.name) {
                    return selectableColumn;
                  }

                  return {
                    name: selectableColumn.name,
                    visible: !selectableColumn.visible,
                  };
                });
                setColumnState && setColumnState(newColumnState);
              },
              selected: column.visible,
              selectable: true,
            };
          }),
        },
      ]}
      filterable={false}
    />
  );

  const activeFilters = Object.values(filterComponents).filter((filter) => {
    return filter.activeCount() !== 0;
  });

  const filteredResults = getFilteredItems(serverGroups, activeFilters, ['name', 'id'], searchQuery ?? '', sortType);

  const filteredColumns = columns
    .map((column) => {
      const matching = columnState.find((stateColumn) => stateColumn.name === column.name);
      if (!matching) {
        return;
      }
      if (matching?.visible !== true) {
        return;
      }

      const asColumn: ExtendedTableColumn<IngestServerGroup & { id: string }> = {
        ...column,
        isActive: column.id === activeHeading,
        setIsActive: (arg) => {
          if (arg) {
            setActiveHeading(column.id as IngestServerGroupsTableColumnKeys);
            return;
          }
          setActiveHeading(undefined);
        },
        hideCallback: () => {
          const newColumnState = columnState.map((selectableColumn) => {
            if (selectableColumn.name !== column.name) {
              return selectableColumn;
            }

            return {
              name: selectableColumn.name,
              visible: !selectableColumn.visible,
            };
          });

          setColumnState && setColumnState(newColumnState);
        },
        columnsContent: columnsSelector,
      };

      return asColumn;
    })
    .filter((column) => column !== undefined) as ExtendedTableColumn<IngestServerGroup & { id: string }>[];

  return (
    <>
      <TableViewLayout
        table={
          <Table
            items={filteredResults.map((serverGroup) => ({ id: serverGroup.id ?? '', ...serverGroup }))}
            columns={filteredColumns}
            frozenFirstColumn={true}
            emptyProps={{
              title: 'No Ingest Server Groups found',
              description: 'Create an Ingest Server Group to get started',
              actions: [],
              placement: 'full-page',
              useCase: 'empty',
            }}
          />
        }
        searchInput={
          <SearchInput
            hasAnyFilters={hasAnyFilters}
            activeFilterCount={activeFilterCount}
            handleResetFilters={handleResetFilters}
            onSearchInputChange={onSearchInputChange}
            categories={categories}
          />
        }
        filterAndColumnControls={<ColumnsPopover columnsSelector={columnsSelector} />}
      />
    </>
  );
};

const IngestServerGroupsTableView: FC = () => {
  const { workspace } = useParams();
  const { data, isLoading, isError } = useGetIngestServerGroups(workspace ?? '');

  if (isLoading) {
    return <Loading />;
  }

  if (isError) {
    return (
      <Empty
        placement="full-page"
        actions={[]}
        useCase="errorUnknown"
        title="Unknown Error"
        description="Error loading ingest server groups"
      />
    );
  }

  return <IngestServerGroupsTableContent serverGroups={data.ingestServerGroups} />;
};

export default IngestServerGroupsTableView;
